import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { Button, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { startCase, toLower } from "lodash";
import React, { useEffect } from "react";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import ComplianceRule from "./ComplianceRule";
import ComplianceRuleModalContainer from "./ComplianceRuleModalContainer";
import operations from "./ducks/operations";

const StyledPaper = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3), // `theme.spacing.unit` is deprecated, use `theme.spacing()`
    borderRadius: "3px",
    "&:focus": {
        outline: "none",
    },
    display: "flex",
    flexDirection: "column",
    width: "65%",
    height: "75vh",
}));

const ComplianceRuleContainer = (props) => {
    const {
        rules,
        addRule,
        saveNewRule,
        updateRule,
        toggleModal,
        modal,
        getConditions,
        handleDragEnd,
        updatePriorities,
        isLoading,
        getRulesByParentEntityTypeKey,
        revertDropdownDisplays,
        parentEntityTypeKey,
        resetRules,
        parentEntityTypes,
        getAllParentEntityTypes,
        elementsSaving,
        resetPriorities,
    } = props;

    useEffect(() => {
        const { id } = props.match.params;
        ReactGA.send({ hitType: "pageview", page: "/parent-type/*", title: `Document Compliance Parent Type: ${id}` });
        getRulesByParentEntityTypeKey(id);
        getConditions(id);
        if (parentEntityTypes.length === 0) {
            getAllParentEntityTypes();
        }
        return () => {
            resetRules();
        };
    }, []);

    const { id } = props.match.params;
    const parentTypeHeader =
        parentEntityTypes.length !== 0
            ? parentEntityTypes.find((parentType) => parentType.parentEntityTypeKey === Number(id)).name
            : "";
    const formattedParentTypeHeader = startCase(toLower(parentTypeHeader));
    const { modalHeader } = modal;
    const handleClose = () => {
        toggleModal(false);
        revertDropdownDisplays();
    };
    const getListStyle = () => ({
        padding: "",
    });

    const onDragEnd = (results) => {
        const { destination, source } = results;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;
        handleDragEnd(results, rules);
    };

    return (
        <React.Fragment>
            <div style={{ display: "flex", margin: "25px 0px", justifyContent: "space-between" }}>
                <div>
                    <h2 className="page-title">{`${formattedParentTypeHeader} Document Compliance`}</h2>
                    <div className="page-subtitle">{`Entity Type Rule Configuration`}</div>
                </div>
                <div>
                    <Button
                        onClick={() => {
                            addRule();
                        }}
                        variant="outlined"
                        color="primary"
                        disabled={props.conditions.length <= 1}
                    >
                        + Add Rule
                    </Button>
                    <Button
                        onClick={() => {
                            updatePriorities(parentEntityTypeKey);
                        }}
                        variant="contained"
                        color="primary"
                        disabled={props.rules.length === 0}
                    >
                        Save Priority
                    </Button>
                    <Button
                        onClick={() => {
                            resetPriorities();
                        }}
                        variant="contained"
                        color="secondary"
                        disabled={props.rules.length === 0}
                    >
                        Reset Priority
                    </Button>
                </div>
            </div>
            {!isLoading && !elementsSaving.getRules ? (
                <div>
                    {props.conditions.length <= 1 ? (
                        <p>
                            Rules cannot be created here because no applicable Conditions exist for{" "}
                            {formattedParentTypeHeader} yet. Please contact the KM Connect IT team to have Conditions
                            created.
                        </p>
                    ) : (
                        <StyledPaper>
                            <div style={{ overflowY: "scroll" }}>
                                <React.Fragment>
                                    {props.rules.length === 0 ? (
                                        <p>
                                            No Rules created for {formattedParentTypeHeader} yet. Create a Rule by
                                            clicking the Add Rule button above.
                                        </p>
                                    ) : (
                                        <DragDropContext onDragEnd={onDragEnd}>
                                            <Droppable droppableId="droppable" style={{ maxWidth: "100%" }}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        {...provided.droppableProps}
                                                        ref={provided.innerRef}
                                                        style={getListStyle()}
                                                    >
                                                        {props.rules.map((rule, index) => {
                                                            return (
                                                                <ComplianceRule {...props} rule={rule} index={index} />
                                                            );
                                                        })}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                    )}
                                </React.Fragment>
                            </div>
                        </StyledPaper>
                    )}
                </div>
            ) : (
                <CircularProgress style={{ position: "fixed", top: "50%", left: "50%" }} />
            )}
            <ComplianceRuleModalContainer
                handleClose={handleClose}
                handleSaveRule={(ruleKey) => {
                    ruleKey ? updateRule(ruleKey, parentEntityTypeKey) : saveNewRule(parentEntityTypeKey);
                }}
                modalHeader={modalHeader}
            />
        </React.Fragment>
    );
};

const mapStateToProps = (state) => ({
    isLoading: state.utilsReducer.isLoading,
    rules: state.complianceReducer.rules,
    modal: state.complianceReducer.modal,
    conditions: state.complianceReducer.conditions.conditionTypes,
    parentEntityTypeKey: state.complianceReducer.conditions.parentEntityTypeKey,
    elementsSaving: state.utilsReducer.elementsSaving,
    parentEntityTypes: state.complianceReducer.parentEntityTypes,
});

const mapDispatchToProps = (dispatch) => ({
    getRulesByParentEntityTypeKey: (parentEntityTypeKey) => {
        dispatch(operations.getRulesByParentEntityTypeKey(parentEntityTypeKey));
    },
    addRule: () => {
        dispatch(operations.addRule());
    },
    editRule: (ruleKey) => {
        dispatch(operations.editRule(ruleKey));
    },
    saveNewRule: (parentEntityTypeKey) => {
        dispatch(operations.saveNewRule(parentEntityTypeKey));
    },
    updateRule: (ruleKey, parentEntityTypeKey) => {
        dispatch(operations.updateRule(ruleKey, parentEntityTypeKey));
    },
    toggleModal: (toggle) => {
        dispatch(operations.toggleModal(toggle));
    },
    getConditions: (parentEntityTypeKey, ruleKey) => {
        dispatch(operations.getConditions(parentEntityTypeKey, ruleKey));
    },
    handleDragEnd: (results, comps) => {
        dispatch(operations.handleDragEnd(results, comps));
    },
    updatePriorities: (parentEntityTypeKey) => {
        dispatch(operations.updatePriorities(parentEntityTypeKey));
    },
    revertDropdownDisplays: () => {
        dispatch(operations.revertDropdownDisplays());
    },
    resetPriorities: () => {
        dispatch(operations.resetPriorities());
    },
    resetRules: () => {
        dispatch(operations.resetRules());
    },
    getAllParentEntityTypes: () => {
        dispatch(operations.getAllParentEntityTypes());
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(ComplianceRuleContainer);
